import api from '../config/api';

let vapidPublicKey = null;

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

export async function getVapidKey() {
  if (vapidPublicKey) return vapidPublicKey;
  try {
    const { data } = await api.get('/push/vapid-public-key');
    vapidPublicKey = data.publicKey;
    return vapidPublicKey;
  } catch {
    return null;
  }
}

export function isPushSupported() {
  return 'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window;
}

export function getPermissionState() {
  if (!('Notification' in window)) return 'unsupported';
  return Notification.permission;
}

export async function subscribeToPush(role = 'client') {
  if (!isPushSupported()) return null;

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') return null;

  const publicKey = await getVapidKey();
  if (!publicKey) return null;

  const registration = await navigator.serviceWorker.ready;
  const existingSub = await registration.pushManager.getSubscription();

  if (existingSub) {
    await sendSubscriptionToServer(existingSub, role);
    localStorage.setItem('push_endpoint', existingSub.endpoint);
    return existingSub;
  }

  try {
    // Remove old endpoint from server if one existed before
    const oldEndpoint = localStorage.getItem('push_endpoint');
    if (oldEndpoint) {
      await api.post('/push/unsubscribe', { endpoint: oldEndpoint }).catch(() => {});
      localStorage.removeItem('push_endpoint');
    }

    const subscription = await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
    await sendSubscriptionToServer(subscription, role);
    localStorage.setItem('push_endpoint', subscription.endpoint);
    return subscription;
  } catch {
    return null;
  }
}

export async function unsubscribeFromPush() {
  const registration = await navigator.serviceWorker.ready;
  const subscription = await registration.pushManager.getSubscription();

  const endpoint = subscription?.endpoint || localStorage.getItem('push_endpoint');

  if (endpoint) {
    try {
      await api.post('/push/unsubscribe', { endpoint });
    } catch { /* ignore */ }
  }

  if (subscription) {
    await subscription.unsubscribe().catch(() => {});
  }

  localStorage.removeItem('push_endpoint');
  return true;
}

async function sendSubscriptionToServer(subscription, role = 'client') {
  try {
    await api.post('/push/subscribe', { subscription: subscription.toJSON(), role });
  } catch (err) {
    console.error('Failed to send subscription to server:', err);
  }
}

export async function isSubscribed() {
  if (!isPushSupported()) return false;
  try {
    const registration = await navigator.serviceWorker.ready;
    const subscription = await registration.pushManager.getSubscription();
    return !!subscription;
  } catch {
    return false;
  }
}
